<template>
  <page-section :title="$t('Profiles_Title_Attributes')">
    <el-button
      v-if="!readonly"
      slot="controls"
      type="primary"
      plain
      icon="el-icon-circle-plus"
      @click="addRow"
      data-qa="assign-attribute-button"
    >
      {{ $t('Profiles_Button_CreateAttribute') }}
    </el-button>

    <rest-list-editable
      :payload="fetchEndpoint"
      :add-endpoint="saveEndpoint"
      :update-endpoint="saveEndpoint"
      ref="rest-list"
      :filterable="true"
      :apply-filters-to-url="false"
    >
      <rest-list-column
        prop="name"
        :title="$t('ProfileAttributes_Attribute_NameLabel')"
        :min-width="180"
        :editable="true"
      >
        <template slot="input-edit" slot-scope="{ model }">
          <el-select
            :disabled="!model.new"
            v-model="model.name"
            value-key="name"
            filterable
            remote
            :placeholder="autocompletePlaceholder"
            :remote-method="autocompleteSearch"
            :loading="searching"
            @focus="autocompleteSearch()"
            data-qa="attribute-name-input"
          >
            <el-option
              v-for="item in options"
              :key="item.name"
              :label="item.name"
              :value="item.name"
              :data-qa="
                `option-${item.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`
              "
            />
          </el-select>
        </template>
      </rest-list-column>

      <rest-list-column
        prop="value"
        :title="$t('ProfileAttributes_Field_Value')"
        :min-width="180"
        :editable="true"
        :tooltip="true"
        :formatter="personalDataFormatter"
      >
        <template slot="input-edit" slot-scope="{ model }">
          <el-input
            v-if="
              [TagType.ARRAY, TagType.OBJECT, TagType.UNENFORCED_JSON].includes(
                model.type
              )
            "
            data-qa="profile-attr-value-input"
            :value="
              typeof model.value === 'object'
                ? JSON.stringify(model.value)
                : model.value
            "
            @input="
              value => {
                model.value = value
              }
            "
            type="textarea"
          />
          <el-input
            v-else
            v-model="model.value"
            type="text"
            data-qa="profile-attr-value-input"
          />
        </template>
      </rest-list-column>

      <rest-list-column-date
        v-if="timestampEnabled"
        prop="timestamp"
        :title="$t('ProfileAttributes_Field_IdentifiedAt')"
        :width="200"
        :unix="false"
        :filterable="true"
      />

      <rest-list-column-actions :editable="true" v-if="!readonly">
        <template slot-scope="scope">
          <rest-list-action-delete
            :name="scope.row.name"
            :endpoint="deleteEndpoint(scope.row)"
          />
        </template>
      </rest-list-column-actions>
    </rest-list-editable>
  </page-section>
</template>

<script>
import PageSection from '@/common/components/page/PageSection'
import RestListColumn from '@/common/components/rest/RestListColumn'
import { projectEndpoint } from '@/core/helpers'
import { TagType } from '@/modules/data-manager/enum/TagTypeEnum'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import RestListEditable from '@/common/components/rest/RestListEditable'
import RestApi from '@/core/services/RestApi'
import memoize from 'memoizee'
import RestListColumnDate from '../../../common/components/rest/RestListColumnDate'
import { $hasFeature } from '@/core/utils/VueAcl'

const fetchAttributes = memoize(
  (queryString, profileID) =>
    RestApi(
      projectEndpoint({
        method: 'get',
        url: `attributes`,
        params: {
          name: queryString,
          attribute_profile: 1,
          exclude_assigned_to_profile: profileID
        }
      })
    ).then(({ data }) => data),
  { promise: true, maxAge: 1000 }
)

export default {
  name: 'ProfileAttributesList',

  components: {
    RestListColumnDate,
    RestListEditable,
    RestListActionDelete,
    RestListColumnActions,
    PageSection,
    RestListColumn
  },

  props: {
    readonly: {
      type: Boolean,
      required: true
    }
  },

  data() {
    return {
      options: [],
      TagType,
      searching: false,
      suggestionsListLength: 1
    }
  },

  computed: {
    fetchEndpoint() {
      const params = {
        sort: 'name'
      }
      if ($hasFeature('tagCategories')) {
        params.has_category = 0
      }
      return projectEndpoint({
        method: 'get',
        url: `profiles/${this.$route.query.profile_id}/attributes`,
        params
      })
    },

    autocompletePlaceholder() {
      if (this.suggestionsListLength < 1) {
        return this.$t('Profiles_Text_AttributesNotAvailable')
      } else {
        return this.$t('Profiles_Text_SelectAttribute')
      }
    },

    timestampEnabled() {
      return this.$store.state.app.features.jsonAttributes !== 2
    }
  },

  methods: {
    saveEndpoint(row) {
      return projectEndpoint({
        method: 'patch',
        url: `profiles/${this.$route.query.profile_id}/attributes/${row.name}`
      })
    },

    deleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `profiles/${this.$route.query.profile_id}/attributes/${row.name}`
      })
    },

    addRow() {
      this.$refs['rest-list'].addRow({
        new: true,
        profile_id: this.$route.query.profile_id,
        type: TagType.STRING
      })
    },

    personalDataFormatter(value) {
      if (value === null || value === '') {
        return '-'
      } else if (this.$can('UserPersonalData::Read')) {
        return value
      } else {
        return '******'
      }
    },

    autocompleteSearch(queryString) {
      this.searching = true
      fetchAttributes(queryString, this.$route.query.profile_id).then(data => {
        this.suggestionsListLength = data.length
        this.options = data.sort(function(a, b) {
          let textA = a.name.toUpperCase()
          let textB = b.name.toUpperCase()
          return textA < textB ? -1 : textA > textB ? 1 : 0
        })
        this.searching = false
      })
    }
  }
}
</script>
<style scoped>
.page-section {
  border: 1px solid #dbdee7;
  padding: 20px;
}
</style>



// WEBPACK FOOTER //
// src/modules/profiles/components/ProfileAttributesList.vue